'use client'

import { AppBar, Box, Theme, styled, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useScroll from '@hooks/useScroll'
import { usePathname, useSearchParams } from 'next/navigation'
import MainNavBarContent from './MainNavBarContent'
import ProposalNavBar from './ProposalNavBar'
import HoldingsNavBar from './HoldingsNavBar'

const AppBarInnerBox = styled(Box)(
  ({
    theme,
    $fullscreen,
    $drawermode,
    $isDaoProposalRoute,
  }: {
    theme?: Theme
    $fullscreen: string
    $drawermode: string
    $isDaoProposalRoute: string
  }) => ({
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: '100%',
    // The bar is the page: no outline, no rounded panel, no line under it. It
    // is told apart from the content by the space around the content, the way
    // the landing site's menu is.
    borderRadius: theme.spacing($fullscreen || $drawermode ? 0 : 2),
    border: 'none',
    backgroundColor: 'var(--dewi_surface_page)',
    overflow: 'visible',
    height: '64px',

    [theme.breakpoints.up('xs')]: {
      height: $drawermode ? '100dvh' : '56px',
    },
    [theme.breakpoints.up('sm')]: {
      height: $drawermode ? '100dvh' : '64px',
    },

    [theme.breakpoints.down('md')]: {
      borderRadius: 0,
      border: 'none',
      height: !$drawermode ? ($isDaoProposalRoute ? '96px' : '56px') : '',
    },
  }),
)

const NavBar = () => {
  const theme = useTheme()
  const pathname = usePathname()
  const params = useSearchParams()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { scrollY } = useScroll()

  const onToggleDrawer = useCallback(() => setDrawerOpen((s) => !s), [])

  const isDaoProposalRoute = useMemo(() => {
    // Check if using /dao/[proposalKey] for example /dao/HvFBAXRf618GeYA8EdTnu4m9i6Cu13Uoe9fDyZypP2xX
    return pathname.includes('/proposals/')
  }, [pathname])

  const isHoldingDetailRoute = useMemo(() => {
    return pathname.includes('/holdings/') && params.get('dewi')
  }, [pathname, params])

  useEffect(() => {
    // Detect theme lg breakpoint
    const lg = theme.breakpoints.values.lg
    const handleResize = () => {
      if (window.innerWidth >= lg) {
        setDrawerOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [theme.breakpoints.values.lg])

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [drawerOpen])

  return (
    <AppBar
      position="fixed"
      sx={{
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        padding: { md: theme.spacing(scrollY === 0 && !drawerOpen ? 2 : 0) },
      }}
    >
      <AppBarInnerBox
        $fullscreen={scrollY !== 0 ? 'true' : undefined}
        $drawermode={drawerOpen ? 'true' : undefined}
        //Rename this prop
        $isDaoProposalRoute={
          isDaoProposalRoute || isHoldingDetailRoute ? 'true' : undefined
        }
      >
        {!isDaoProposalRoute ? (
          !isHoldingDetailRoute ? (
            <MainNavBarContent
              onToggleDrawer={onToggleDrawer}
              drawerOpen={drawerOpen}
            />
          ) : (
            <HoldingsNavBar />
          )
        ) : (
          <ProposalNavBar />
        )}
      </AppBarInnerBox>
    </AppBar>
  )
}

export default NavBar
